import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiError, ApiErrorCode } from '@interfaces/httpResponses';
import { ACCESS_ERROR_MESSAGES } from '../access-errors';

@Injectable({ providedIn: 'root' })
export class ApiErrorAdapter {
  from(error: unknown, fallbackMessage?: string): ApiError {
    if (typeof error === 'string' && error.trim()) {
      return {
        code: ApiErrorCode.UNKNOWN_ERROR,
        message: error.trim(),
        status: 0,
      };
    }

    if (!(error instanceof HttpErrorResponse)) {
      const externalError = this.externalError(error);
      if (externalError) {
        return {
          code: externalError.code,
          message: externalError.message,
          status: 0,
        };
      }

      return {
        code: ApiErrorCode.UNKNOWN_ERROR,
        message: fallbackMessage ?? 'Ocurrió un error inesperado.',
        status: 0,
      };
    }
    if (error.status === 0) {
      return {
        code: ApiErrorCode.NETWORK_ERROR,
        message: 'No pudimos conectar con el servidor. Revisá tu conexión.',
        status: 0,
      };
    }
    const body = error.error;
    const record = this.isRecord(body) ? body : null;
    const isNormalizedEnvelope =
      record?.['status'] === 'Error' || record?.['success'] === false;
    const message = isNormalizedEnvelope
      ? this.safeString(record?.['message'])
      : '';
    const code =
      (isNormalizedEnvelope ? this.safeString(record?.['code']) : '') ||
      ApiErrorCode.UNKNOWN_ERROR;
    return {
      code,
      message:
        ACCESS_ERROR_MESSAGES[code] ||
        message ||
        fallbackMessage ||
        this.fallback(error.status),
      status: error.status,
    };
  }

  private externalError(
    error: unknown,
  ): { code: string; message: string } | null {
    if (!this.isRecord(error)) return null;

    const code = this.safeString(error['code']);
    const messages: Record<string, string> = {
      'auth/popup-closed-by-user':
        'Se cerró la ventana de Google antes de completar el acceso.',
      'auth/popup-blocked':
        'El navegador bloqueó la ventana de Google. Habilitá las ventanas emergentes e intentá nuevamente.',
      'auth/network-request-failed':
        'No se pudo conectar con Google. Revisá tu conexión e intentá nuevamente.',
    };
    const message = messages[code];

    return message ? { code, message } : null;
  }

  private fallback(status: number): string {
    if (status === 401) return 'Necesitás iniciar sesión para continuar.';
    if (status === 403) return 'No tenés permiso para realizar esta acción.';
    if (status >= 500) return 'El servidor no pudo completar la operación.';
    return 'No pudimos completar la operación.';
  }

  private safeString(value: unknown): string {
    return typeof value === 'string' && value.trim() ? value.trim() : '';
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }
}
